import $t from '@Ravnur-Inc/l10n/$t';
import { StandardOption } from '@Ravnur-Inc/shared/types/Component';
import { computed, ref } from 'vue';
import {
  BaseTimedataRepository,
  TimedataAbbreviation,
  useTimedataEditor,
} from './useTimedataEditor';
import { TimedataSegment } from '@Ravnur-Inc/shared/types/Media';

const MIN_SPLIT_DURATION = 2;
export const ROW_HEIGHT = 45;

export enum SEGMENT_ACTIONS {
  SHIFT = 'shift',
  SPLIT = 'split',
  DELETE = 'delete',
}

export enum CAPTION_ACTIONS {
  CHANGE_LANGUAGE = 'change-language',
  DOWNLOAD_CAPTION = 'download-caption',
  DOWNLOAD_TRANSCRIPT = 'download-transcript',
  DELETE = 'delete',
}

export type SplitError = { row: number; text: string };

export const useCaptionsEditor = (repository: BaseTimedataRepository) => {
  const splitError = ref<Nullable<SplitError>>(null);
  const splitErrorTimeout = ref<Nullable<number>>(null);
  const initialCCJobId = ref<Nullable<string>>(null);
  const initialJobFailed = ref(false);
  const offset = ref(0);

  const timedataCtx = useTimedataEditor(TimedataAbbreviation.CAPTION, repository);

  const segmentActions: StandardOption[] = [
    {
      id: SEGMENT_ACTIONS.SHIFT,
      label: $t('video-editors', 'cc-editor__shift-timing'),
    },
    {
      id: SEGMENT_ACTIONS.SPLIT,
      label: $t('video-editors', 'cc-editor__split-segment'),
    },
    {
      id: SEGMENT_ACTIONS.DELETE,
      label: $t('video-editors', 'cc-editor__delete-segment'),
    },
  ];

  const actions = computed<StandardOption[]>(() => [
    {
      id: CAPTION_ACTIONS.CHANGE_LANGUAGE,
      label: $t('video-editors', 'cc-editor__change-caption-language'),
      disabled: initialJobFailed.value,
    },
    {
      id: CAPTION_ACTIONS.DOWNLOAD_CAPTION,
      label: $t('video-editors', 'cc-editor__download-caption'),
      disabled: initialJobFailed.value,
    },
    {
      id: CAPTION_ACTIONS.DOWNLOAD_TRANSCRIPT,
      label: $t('video-editors', 'cc-editor__download-transcript'),
      disabled: initialJobFailed.value,
    },
    {
      id: CAPTION_ACTIONS.DELETE,
      label: $t('video-editors', 'cc-editor__delete-caption'),
      disabled: !timedataCtx.selectedTimedata.value || initialJobFailed.value,
    },
  ]);

  const inViewPort = (idx: number) => {
    return offset.value - 10 < idx && idx < offset.value + 10;
  };

  const rowError = (row: number): SplitError | null => {
    if (splitError.value && splitError.value.row === row) {
      return splitError.value;
    } else {
      return null;
    }
  };

  const shiftSegment = async (time: number, segment: TimedataSegment, row: number) => {
    const forward = time > segment.from;
    const diff = forward ? time - segment.from : segment.from - time;

    let subsequentSegments = timedataCtx.selectedTimedata.value?.segments?.slice(row);

    if (!subsequentSegments) {
      return;
    }

    if (forward) {
      subsequentSegments = subsequentSegments.map((s) => ({
        ...s,
        from: s.from + diff,
        to: s.to + diff,
      }));
    } else {
      subsequentSegments = subsequentSegments.map((s) => ({
        ...s,
        from: s.from - diff,
        to: s.to - diff,
      }));
    }

    timedataCtx.selectedTimedata.value = {
      ...timedataCtx.selectedTimedata.value,
      segments: [
        ...(timedataCtx.selectedTimedata.value?.segments?.slice(0, row) || []),
        ...subsequentSegments,
      ],
    } as any;
  };

  const splitSegment = (row: number) => {
    if (!timedataCtx.selectedTimedata.value || !timedataCtx.selectedTimedata.value.segments) return;

    const segment = timedataCtx.selectedTimedata.value.segments[row];
    const duration = segment.to - segment.from;

    if (duration < MIN_SPLIT_DURATION) {
      splitError.value = {
        row,
        text: $t('video-editors', 'cc-editor__caption-split-validation', {
          count: MIN_SPLIT_DURATION,
        }),
      };

      if (splitErrorTimeout.value) {
        clearTimeout(splitErrorTimeout.value);
      }

      splitErrorTimeout.value = setTimeout(() => {
        splitError.value = null;
      }, 4000) as any;

      return;
    }

    const newDuration = duration / 2;

    timedataCtx.selectedTimedata.value.segments.splice(row, 1, {
      id: segment.id,
      from: segment.from,
      to: segment.from + newDuration,
      text: segment.text,
    });

    timedataCtx.selectedTimedata.value.segments.splice(row + 1, 0, {
      id: '',
      from: segment.from + newDuration,
      to: segment.to,
      text: segment.text,
    });
  };

  const download = (src: string, name: string) => {
    const link = document.createElement('a');

    link.href = src;
    link.download = name;
    link.target = '_blank';
    link.click();
  };

  return {
    ...timedataCtx,

    // data
    initialCCJobId,
    initialJobFailed,
    offset,
    actions,
    splitError,

    // getter
    segmentActions,

    // actions
    inViewPort,
    rowError,
    shiftSegment,
    splitSegment,
    download,
  };
};
